import type { ImbalancePriceInsight } from "../../../services/client";
import { ONE_HOUR } from "../../../util/time";
import type { PricePoint } from "./useDayAheadPrices";

export type SpreadRow = {
  timestamp: number;
  actual: number | null;
  forecast: number | null;
  dayAhead: number | null;
};

// Day-ahead prices are quarter-hourly since the 15-minute auction went live and
// hourly before it, so a quarter hour without its own price takes its hour's.
export const spreadRows = (
  insights: ImbalancePriceInsight[],
  dayAhead: PricePoint[],
): SpreadRow[] => {
  const dayAheadByTime = new Map(
    dayAhead.map((point) => [point.timestamp.getTime(), point.value]),
  );
  return insights.map((insight) => {
    const timestamp = new Date(insight.timestamp).getTime();
    return {
      timestamp,
      actual: insight.actual_price,
      forecast: insight.latest_prediction,
      dayAhead:
        dayAheadByTime.get(timestamp) ??
        dayAheadByTime.get(timestamp - (timestamp % ONE_HOUR)) ??
        null,
    };
  });
};

export const spread = (price: number | null, dayAhead: number | null) =>
  price === null || dayAhead === null ? null : price - dayAhead;

// The forecast picks the right side when it puts the imbalance price on the
// same side of the day-ahead price as it turned out: that decides whether
// deviating from the nomination by injecting or by taking off earns money.
export const sideScores = (rows: SpreadRow[]) => {
  const compared = rows.flatMap(({ actual, forecast, dayAhead }) => {
    const actualSpread = spread(actual, dayAhead);
    const forecastSpread = spread(forecast, dayAhead);
    return actualSpread === null || forecastSpread === null
      ? []
      : [
          {
            right: Math.sign(actualSpread) === Math.sign(forecastSpread),
            worth: Math.abs(actualSpread),
          },
        ];
  });
  const totalWorth = compared.reduce((sum, row) => sum + row.worth, 0);
  return {
    quarterHours: compared.length,
    rightShare:
      compared.filter((row) => row.right).length / (compared.length || 1),
    rightWorthShare:
      compared.reduce((sum, row) => sum + (row.right ? row.worth : 0), 0) /
      (totalWorth || 1),
  };
};
